import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL, formatDate } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../context/AuthContext';
import { MessageSquare, Pencil, Clock, CheckCircle2, XCircle, AlertTriangle, Eye } from 'lucide-react';

const STATUS_META = {
  pending_review: { label: 'Pending Review', cls: 'bg-amber-100 text-amber-700', Icon: Clock },
  published: { label: 'Published', cls: 'bg-emerald-100 text-emerald-700', Icon: CheckCircle2 },
  locked: { label: 'Published (Locked)', cls: 'bg-emerald-100 text-emerald-700', Icon: CheckCircle2 },
  needs_revision: { label: 'Needs Revision', cls: 'bg-orange-100 text-orange-700', Icon: AlertTriangle },
  rejected: { label: 'Not Approved', cls: 'bg-red-100 text-red-700', Icon: XCircle },
  hidden: { label: 'Hidden', cls: 'bg-gray-100 text-gray-600', Icon: Eye },
  removed: { label: 'Removed', cls: 'bg-gray-100 text-gray-600', Icon: XCircle },
};

const Badge = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.pending_review;
  const { Icon } = m;
  return <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${m.cls}`}><Icon className="w-3 h-3" /> {m.label}</span>;
};

const MyCommunityActivity = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [data, setData] = useState({ questions: [], answers: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => { document.title = 'My Community Activity | Ask a Social Worker'; }, []);
  useEffect(() => { if (!authLoading && !isAuthenticated) navigate('/login'); }, [authLoading, isAuthenticated, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await axios.get(`${API_URL}/community/my-questions`); setData(r.data); } catch (e) { /* noop */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-[#F7F5EF] pb-16">
      <section className="bg-primary pt-24 pb-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-white flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-serif text-3xl font-bold">My Community Activity</h1>
            <p className="text-white/80 mt-2">Track your questions and responses.</p>
          </div>
          <Button onClick={() => navigate('/community/ask')} className="bg-secondary hover:bg-secondary-hover text-white" data-testid="activity-ask-btn">Ask a Question</Button>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="py-16 text-center text-gray-500">Loading…</div>
        ) : (
          <>
            <h2 className="font-serif text-xl font-bold text-primary mb-3">My Questions ({data.questions.length})</h2>
            <div className="space-y-3">
              {data.questions.length === 0 && <div className="bg-white rounded-2xl border p-6 text-center text-gray-500 text-sm">You haven't asked any questions yet.</div>}
              {data.questions.map((q) => (
                <div key={q.question_id} className="bg-white rounded-2xl border p-5" data-testid={`my-question-${q.question_id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-semibold text-secondary uppercase">{q.category}</span>
                        <Badge status={q.status} />
                      </div>
                      <h3 className="font-serif text-lg font-bold text-primary leading-tight mt-1">{q.title}</h3>
                      <p className="text-sm text-gray-600 mt-1 line-clamp-2">{q.body}</p>
                      {q.moderator_note && (q.status === 'needs_revision' || q.status === 'rejected') && (
                        <div className="mt-2 bg-orange-50 border border-orange-200 rounded-lg p-2 text-xs text-orange-800"><strong>Moderator note:</strong> {q.moderator_note}</div>
                      )}
                      <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                        <span>{formatDate(q.created_at)}</span>
                        <span className="flex items-center gap-1"><MessageSquare className="w-3.5 h-3.5" /> {q.answer_count || 0}</span>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      {(q.status === 'published' || q.status === 'locked') && (
                        <Link to={`/community/questions/${q.question_id}`}><Button size="sm" variant="outline" data-testid={`view-${q.question_id}`}>View</Button></Link>
                      )}
                      {(q.status === 'pending_review' || q.status === 'needs_revision') && (
                        <Button size="sm" variant="outline" onClick={() => navigate(`/community/ask?edit=${q.question_id}`)} data-testid={`edit-${q.question_id}`}><Pencil className="w-3.5 h-3.5 mr-1" /> Edit</Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <h2 className="font-serif text-xl font-bold text-primary mt-8 mb-3">My Responses ({data.answers.length})</h2>
            <div className="space-y-3">
              {data.answers.length === 0 && <div className="bg-white rounded-2xl border p-6 text-center text-gray-500 text-sm">You haven't posted any responses yet.</div>}
              {data.answers.map((a) => (
                <div key={a.answer_id} className="bg-white rounded-2xl border p-5" data-testid={`my-answer-${a.answer_id}`}>
                  <div className="flex items-center gap-2 mb-1"><Badge status={a.status} /></div>
                  <p className="text-sm text-gray-700 line-clamp-3">{a.body}</p>
                  <Link to={`/community/questions/${a.question_id}`} className="text-xs text-secondary hover:underline mt-2 inline-block">View discussion</Link>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MyCommunityActivity;
